/**
 * This contains helper functions for color
 *
 * @author Afifa Saleem <afifa@250mils.com>
 */
import type {Instance} from 'tinycolor2';
import tinycolor from 'tinycolor2';
import Values from 'values.js';
import {type FillObject, FillTypes} from '@PosterWhiteboard/classes/fill.types';
import {ColorFillPropertyType} from '@Components/color-fill';
import type {ColorStorage} from '@Components/color-picker-v2';
import {COLOR_OBJECT_IDS} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import isObject from 'lodash/isObject';
import remove from 'lodash/remove';
import type {TSerializedFabricColor} from '@PosterWhiteboard/items/item/item.types';

export interface GradientColors {
  color1: RGB;
  color2: RGB;
}

export const PMW_COLORS_NEUTRAL = {
  NEUTRAL_0: '#FFFFFF',
  NEUTRAL_1: '#FAFAFA',
  NEUTRAL_3: '#F2F2F5',
  NEUTRAL_6: '#E6E6EC',
  NEUTRAL_11: '#DCDCE1',
  NEUTRAL_18: '#CBCBD0',
  NEUTRAL_28: '#A1A1AA',
  NEUTRAL_70: '#403F5D',
  NEUTRAL_90: '#141325',
};

export const PMW_COLORS_PRIMARY = {
  PRIMARY_50: '#9FDDF3',
  PRIMARY_100: '#3FBCE8',
  PRIMARY_150: '#23A6D5',
  PRIMARY_200: '#00688C',
};

export const PMW_COLORS_100 = {
  PRIMARY: '#3FBCE7',
  SECONDARY: '#9577E7',
  SUCCESS: '#24C19C',
};

export const PMW_COLORS_SECONDARY = {
  SECONDARY_10: '#F4F1FD',
  SECONDARY_200: '#4017AF',
};

const READABILITY_RATIO_THRESHOLD_FOR_WHITE = 2.35;
const READABILITY_RATIO_THRESHOLD_FOR_COLOR = 3;
export type RGB = [number, number, number];
export type RGBA = [number, number, number, number];

export interface Rgbacolor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export enum ColorTone {
  VERY_DARK = 'very-dark',
  VERY_LIGHT = 'very-light',
  MID = 'mid',
  DARK = 'dark',
  LIGHT = 'light',
}

export interface HoverableContainerColors {
  backgroundColor: string;
  graphicContainerBg: string;
  graphicContainerHoverBg: string;
  textColor: string;
  hoverColor: string;
  srcColor: string;
}

export const rgbToHex = (color: RGB, alpha?: number): string => {
  if (alpha === undefined) {
    return tinycolor({
      r: color[0],
      g: color[1],
      b: color[2],
    }).toHex();
  }

  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
    a: alpha,
  }).toHex8();
};

export const rgbaToHex = (color: RGBA, shouldIncludeAlpha = true): string => {
  if (shouldIncludeAlpha) {
    return tinycolor({
      r: color[0],
      g: color[1],
      b: color[2],
      a: color[3],
    }).toHex8();
  }
  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
  }).toHex();
};

export const rgbaArrayToRgbString = (color: RGBA, shouldIncludeAlpha = true): string => {
  if (shouldIncludeAlpha) {
    return tinycolor({
      r: color[0],
      g: color[1],
      b: color[2],
      a: color[3],
    }).toRgbString();
  }
  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
  }).toRgbString();
};

export const rgbaToHexString = (color: RGBA): string => {
  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
    a: color[3] ?? 1,
  }).toHex8String();
};

const NEAR_WHITE_CHANNEL_THRESHOLD = 240;

/** True when a colour is white/near-white — used to keep a border on a swatch that would otherwise blend into a white surface. */
export const isNearWhiteColor = (color: RGBA): boolean => {
  return color[0] >= NEAR_WHITE_CHANNEL_THRESHOLD && color[1] >= NEAR_WHITE_CHANNEL_THRESHOLD && color[2] >= NEAR_WHITE_CHANNEL_THRESHOLD;
};

export const rgbToHexString = (color: RGB, alpha?: number): string => {
  if (alpha === undefined) {
    return tinycolor({
      r: color[0],
      g: color[1],
      b: color[2],
    }).toHexString();
  }

  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
    a: alpha,
  }).toHex8String();
};

export const rgbToRGBString = (color: RGB, alpha?: number): string => {
  if (alpha === undefined) {
    return tinycolor({
      r: color[0],
      g: color[1],
      b: color[2],
    }).toRgbString();
  }

  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
    a: alpha,
  }).toRgbString();
};

export const rgbaToRGBAString = (color: RGBA): string => {
  return tinycolor({
    r: color[0],
    g: color[1],
    b: color[2],
    a: color[3],
  }).toRgbString();
};
export const hextToHexString = (color: string): string => {
  return `#${color}`;
};

export const hexStringToHext = (color: string): string => {
  if (color.length > 1 && color.startsWith('#')) {
    return color.slice(1);
  }

  return color;
};

export const hexToRgb = (hex: string): RGB => {
  const dec = parseInt(hex.includes('#') ? hex.substring(1) : hex, 16);
  return [dec >> 16, (dec & 0x00ff00) >> 8, dec & 0x0000ff];
};

export const hexToRgba = (hex: string, alpha = 1): RGBA => {
  const dec = parseInt(hex.includes('#') ? hex.substring(1) : hex, 16);
  return [dec >> 16, (dec & 0x00ff00) >> 8, dec & 0x0000ff, alpha];
};

export const hexStringToRgba = (hex: string): RGBA => {
  const cleaned = hex.replace('#', '');

  if (cleaned.length === 6) {
    // #RRGGBB
    return hexToRgba('#' + cleaned, 1);
  } else if (cleaned.length === 8) {
    // #RRGGBBAA
    const rgbPart = '#' + cleaned.slice(0, 6);
    const alphaHex = cleaned.slice(6);
    const alpha = parseInt(alphaHex, 16) / 255;
    return hexToRgba(rgbPart, alpha);
  }

  throw new Error(`Invalid hex string: ${hex}`);
};

export const colorToRgbaArray = (color: string): RGBA => {
  const rgb = tinycolor(color).toRgb();
  return [rgb.r, rgb.g, rgb.b, rgb.a];
};

export const convertColorPickerColorsToRGBString = (colors: ColorStorage[]): string[] => {
  return colors.map((colorStorage) => rgbaArrayToRgbString(colorStorage.color));
};

export const convertRgbaArrayColorsToRGBString = (colors: RGBA[]): string[] => {
  return colors.map((color) => rgbaArrayToRgbString(color));
};

export const rgbaToRgbacolor = (color: RGBA): Rgbacolor => {
  return {
    r: color[0],
    g: color[1],
    b: color[2],
    a: color[3] ?? 1,
  };
};

export const rgbacolorToRgba = (color: Rgbacolor): RGBA => {
  return [color.r, color.g, color.b, color.a];
};

/**
 * Get hex color value provided color in any format
 * @param {*} color
 */
export const colorToHex = (color: tinycolor.ColorInput) => {
  return tinycolor(color).toHex();
};

/**
 * returns any color into a formatted hex string in the format: #000000
 * @param color
 * @returns {string}
 */
export const colorToHexString = (color: tinycolor.ColorInput) => {
  return tinycolor(color).toHexString();
};

/**
 * returns any color into a formatted hex 8 string in the format: #aa000000
 * @param color
 * @returns {string}
 */
export const colorToHex8String = (color: tinycolor.ColorInput): string => {
  return tinycolor(color).toHex8String();
};

/**
 * Remove characters not allowed in hex format string
 */
export const removeUnallowedCharacters = (color: string): string => {
  return color ? color.replace(/[^a-fA-F0-9]+/, '') : '';
};

/**
 * Check if color string has 3/6 characters allowed for hex string
 */
export const isCorrectHexFormat = (color: string) => {
  return /^([A-Fa-f0-9]{6})$/i.test(color);
};

/**
 * Get formatted hex value for color
 */
export const getFormattedHexColor = (color: string) => {
  let newColor = color;
  if (!isCorrectHexFormat(color)) {
    newColor = color = removeUnallowedCharacters(color);
    if (color.length < 4) {
      newColor = color[0] ? color[0] + color[0] : '0';
      newColor = color[1] ? newColor + color[1] + color[1] : newColor;
      newColor = color[2] ? newColor + color[2] + color[2] : newColor;
    }
    newColor = `${newColor}000000`.slice(0, 6);
  }
  return newColor;
};

/**
 * generates a color palette with lighter shades of the input color based on the provided weight
 * the higher the weight, the lighter the output colors
 * @param {string} inputColor
 * @param {number} weight
 * @returns {Array}
 */
export const generateColorPaletteForWeight = (inputColor: tinycolor.ColorInput, weight: number) => {
  const color = colorToHexString(inputColor);
  const colorPalette = new Values(color);
  const tints = colorPalette.tints(weight);
  const tintHexes = tints.map((tint) => {
    return tint.hexString();
  });

  return remove(tintHexes, tintHexes.length - 1);
};

/**
 * Generates a color palette with lighter tones than the input color using '20' as a weight
 * This generates 5 colors.
 * @param {string} inputColor
 * @returns {Array}
 */
export const generateLighterToneColorPaletteForColor = (inputColor: tinycolor.ColorInput) => {
  return generateColorPaletteForWeight(inputColor, 20);
};

export const rgbaToRgbaString = (RGBA: RGBA): string => {
  if (RGBA && RGBA.length > 3) {
    return `rgba(${RGBA[0]},${RGBA[1]},${RGBA[2]},${RGBA[3]})`;
  }
  return '';
};

export const getRgbLuminance = (rgb: number[]) => {
  return ((0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255) * 100;
};

/**
 * Rec. 601 perceived brightness of an 8-bit RGB triplet, normalized to 0..1.
 */
export const getPerceivedBrightness = (r: number, g: number, b: number): number => {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

/**
 * Gives a lighter/darker shade of a hex color string (format: #000000)
 *
 * @returns {string}
 * @param color
 * @param factor
 */

export const lightenDarkenColor = (color: RGBA, factor = 100): string => {
  const usePound = false;
  const luminanceFactor = getRgbLuminance(color) > 50 ? factor * -1 : factor;
  const hex = rgbaToHex(color, false);

  const num = parseInt(hex, 16);
  let r = (num >> 16) + luminanceFactor;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + luminanceFactor;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + luminanceFactor;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + `000000${(g | (b << 8) | (r << 16)).toString(16)}`.slice(-6);
};

/**
 * This function is specifically for the new poster editor
 * It converts the fill type property of a graphic item to color fill property type which is then used in color picker v2
 */
export const getColorFillTypeFromFillType = (fillType: FillTypes): ColorFillPropertyType | undefined => {
  switch (fillType) {
    case FillTypes.LINEAR_GRADIENT:
      return ColorFillPropertyType.LINEAR;
    case FillTypes.RADIAL_GRADIENT:
      return ColorFillPropertyType.RADIAL;
    case FillTypes.SOLID:
      return ColorFillPropertyType.SOLID;
    case FillTypes.NONE:
      return undefined;
    default:
      throw new Error('Unhandled fill type in getColorFillTypeFromFillType');
  }
};

/**
 * This function is specifically for the new poster editor
 * It converts the color fill property type to the fill type property of a graphic item
 */
export const getFillTypeFromColorFillType = (colorFillType: ColorFillPropertyType): FillTypes => {
  switch (colorFillType) {
    case ColorFillPropertyType.SOLID:
      return FillTypes.SOLID;
    case ColorFillPropertyType.LINEAR:
      return FillTypes.LINEAR_GRADIENT;
    case ColorFillPropertyType.RADIAL:
      return FillTypes.RADIAL_GRADIENT;
    default:
      throw new Error('unhandled color fill property type');
  }
};

/**
 * This function is specifically for the new poster editor
 * It converts color storage object to Array of RGBA which is the actual type for fillColor for graphic items containing fill
 */
export const getRGBAArrayFromColorStorage = (array: ColorStorage[]): RGBA[] => {
  const rgbaArray: RGBA[] = [];
  for (let index = 0; index < array.length; index += 1) {
    rgbaArray.push(array[index].color);
  }
  return rgbaArray;
};

/**
 * This function is specifically for the new poster editor
 * It converts fillColor RGB Array to color storage object which is then passed to the color picker
 */
export const getColorObjectArrayFromFillColor = (fillColor: RGBA[]): ColorStorage[] => {
  const colorObjectArray: ColorStorage[] = [];
  for (let index = 0; index < fillColor.length; index += 1) {
    colorObjectArray.push({
      id: COLOR_OBJECT_IDS[index],
      color: fillColor[index],
    });
  }
  return colorObjectArray;
};

/**
 * Generates two colors, color2 and color3, based on the user-selected color (color1).
 * If color1 is determined to be a light color, color2 will be darker, and color3 will be even darker.
 * If color1 is determined to be a dark color, color2 will be lighter, and color3 will be even lighter.
 * The degree of darkness or lightness is controlled by the shadeIncrement factor.
 *
 * @param {string} color1 - The hex color code selected by the user.
 * @returns {string[]} An array containing three hex color codes: [color1, color2, color3].
 *                    Color2 and color3 are incrementally darker or lighter than color1 based on its shade.
 *                    Returns null if the input color format is invalid.
 */
export const generateIncrementalShadedColors = (color1: string): string[] => {
  // Ensure the input is a valid hex color
  const hexRegex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

  if (!hexRegex.test(color1)) {
    throw new Error('Invalid color passed to generateIncrementalShadedColors()');
  }

  // Determine if the user-selected color is light or dark based on perceived brightness
  const perceivedBrightness = getPerceivedBrightness(parseInt(color1.slice(1, 3), 16), parseInt(color1.slice(3, 5), 16), parseInt(color1.slice(5, 7), 16));
  const isLightColor = perceivedBrightness > 0.5;

  // Calculate factors for incremental shades
  const shadeIncrement = 0.35;
  const color2Factor = isLightColor ? 1 - shadeIncrement : 1 + shadeIncrement;
  const color3Factor = isLightColor ? 1 - shadeIncrement * 1.5 : 1 + shadeIncrement * 2;

  // Function to adjust brightness
  function adjustBrightness(hex: string, factor: number): string {
    // Parse the hex color to RGB
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Adjust each component based on the factor
    const adjustedR = Math.min(255, Math.max(0, Math.round(r * factor)));
    const adjustedG = Math.min(255, Math.max(0, Math.round(g * factor)));
    const adjustedB = Math.min(255, Math.max(0, Math.round(b * factor)));

    // Convert back to hex
    return `#${[adjustedR, adjustedG, adjustedB]
      .map((value) => {
        return value.toString(16).padStart(2, '0');
      })
      .join('')}`;
  }

  // Generate color2 and color3
  const color2 = adjustBrightness(color1, color2Factor);
  const color3 = adjustBrightness(color1, color3Factor);

  return [color1, color2, color3];
};

export const darkenColor = (colorHex: string, percentage = 20): string => {
  return tinycolor(colorHex).darken(percentage).toHexString();
};

export const lightenColor = (colorHex: string, percentage = 20): string => {
  return tinycolor(colorHex).lighten(percentage).toHexString();
};

export const getRgbaArrayFromFabricColor = (color: TSerializedFabricColor): RGBA[] => {
  if (!color) return [];
  if (typeof color === 'string') return [colorToRgbaArray(color)];

  if (color.type === 'linear' || color.type === 'radial') {
    return color.colorStops.map((stop) => colorToRgbaArray(stop.color));
  }

  return [];
};

export const getColorStringsFromFabricColor = (color: TSerializedFabricColor): string[] => {
  if (!color) return [];
  if (typeof color === 'string') return [color];

  if (color.type === 'linear' || color.type === 'radial') {
    return color.colorStops.map((stop) => stop.color);
  }

  return [];
};

export const applyAlphaToFabricColor = (color: TSerializedFabricColor, alpha: number): TSerializedFabricColor => {
  if (!color) return null;
  if (typeof color === 'string') return tinycolor(color).setAlpha(alpha).toRgbString();

  if (color.type === 'linear' || color.type === 'radial') {
    const updated = {...color};
    updated.colorStops = updated.colorStops.map((colorStop) => ({
      ...colorStop,
      color: tinycolor(colorStop.color).setAlpha(alpha).toRgbString(),
    }));
    return updated;
  }

  throw new Error(`Unhandled color: ${color}`);
};

export const getAlphaFromFabricColor = (color: TSerializedFabricColor): number => {
  if (!color) return 0;
  if (typeof color === 'string') return tinycolor(color).getAlpha();

  if ((color.type === 'linear' || color.type === 'radial') && color.colorStops.length > 0) {
    return tinycolor(color.colorStops[0].color).getAlpha();
  }

  return 0;
};

export const convertFabricColorToFillObject = (color: TSerializedFabricColor): FillObject => {
  if (!color) {
    return {
      fillType: FillTypes.NONE,
      fillColor: [[0, 0, 0, 1]],
    };
  }

  if (typeof color === 'string') {
    return {
      fillType: FillTypes.SOLID,
      fillColor: [colorToRgbaArray(color)],
    };
  }

  if (color.type === 'linear' || color.type === 'radial') {
    const fillType = color.type === 'linear' ? FillTypes.LINEAR_GRADIENT : FillTypes.RADIAL_GRADIENT;
    const fillColor: RGBA[] = [];

    for (const colorStop of color.colorStops) {
      fillColor.push(colorToRgbaArray(colorStop.color));
    }

    return {fillType, fillColor};
  }

  throw new Error(`Unhandled fill: ${JSON.stringify(color)}`);
};

export const getColorPickerColorTypeFromFabricColor = (color: TSerializedFabricColor): undefined | ColorFillPropertyType => {
  if (!color) {
    return undefined;
  }

  if (typeof color === 'string') {
    return ColorFillPropertyType.SOLID;
  }

  switch (color.type) {
    case 'linear':
      return ColorFillPropertyType.LINEAR;
    case 'radial':
      return ColorFillPropertyType.RADIAL;
    default:
      throw new Error(`Unknown color type: ${JSON.stringify(color)}`);
  }
};

export const convertFabricColorToColorPickerColor = (color: TSerializedFabricColor): ColorStorage[] => {
  if (!color) {
    return [];
  }

  if (typeof color === 'string') {
    return [
      {
        id: COLOR_OBJECT_IDS[0],
        color: hexToRgba(tinycolor(color).toHexString(), tinycolor(color).getAlpha()),
      },
    ];
  }

  const colorObjectArray: ColorStorage[] = [];
  if (isObject(color)) {
    for (const [index, colorStop] of color.colorStops.entries()) {
      if (COLOR_OBJECT_IDS[index] === undefined) {
        throw new Error(`Unhandled index in color picker, index: ${index}`);
      }

      colorObjectArray.push({
        id: COLOR_OBJECT_IDS[index],
        color: colorToRgbaArray(colorStop.color),
      });
    }
  }
  return colorObjectArray;
};

export const isSameColor = (a: RGB, b: RGB): boolean => {
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
};

export const SIMILAR_COLOR_DISTANCE_THRESHOLD = 24;

export const getColorDistance = (a: string, b: string): number => {
  const rgbA = tinycolor(a).toRgb();
  const rgbB = tinycolor(b).toRgb();
  const dr = rgbA.r - rgbB.r;
  const dg = rgbA.g - rgbB.g;
  const db = rgbA.b - rgbB.b;
  const rMean = (rgbA.r + rgbB.r) / 2;
  return Math.sqrt((2 + rMean / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMean) / 256) * db * db);
};

export const filterSimilarColors = (colors: string[], threshold = SIMILAR_COLOR_DISTANCE_THRESHOLD): string[] => {
  const kept: string[] = [];
  for (const color of colors) {
    if (kept.every((keptColor) => getColorDistance(color, keptColor) > threshold)) {
      kept.push(color);
    }
  }
  return kept;
};

export const rgbFromRgba = (rgbaColor: RGBA): RGB => {
  return [rgbaColor[0], rgbaColor[1], rgbaColor[2]];
};

export const isFullyTransparent = (rgbaColor: RGBA): boolean => {
  return rgbaColor[3] === 0;
};

export const darkenColorTone = (src: Instance, percentage = 0.15): Instance => {
  const hsl = src.toHsl();
  return adjustColorTone(src, Math.max(0, hsl.l - percentage));
}

export const getDarkenedColorToneHex = (colorHex: string, percentage = 0.15): string => {
  const src = tinycolor(colorHex);
  const darkened = darkenColorTone(src, percentage);
  return darkened.toHexString();
}


export const getColorPaletteForContainer = (baseColorHex: string): HoverableContainerColors => {
  const src = tinycolor(baseColorHex);
  const toneClass = classifyColorTone(src);

  let outerBgL;
  let imageBgL;
  let hoverBgL;
  let imageBgHover;
  let textL;

  switch (toneClass) {
    case ColorTone.DARK:
    case ColorTone.VERY_DARK:
      outerBgL = 0.96;
      imageBgL = 0.93;
      hoverBgL = 0.93;
      imageBgHover = 0.89;
      textL = 0.18;
      break;

    case ColorTone.VERY_LIGHT:
    case ColorTone.LIGHT:
      outerBgL = 0.95;
      imageBgL = 0.92;
      hoverBgL = 0.92;
      imageBgHover = 0.89;
      textL = 0.18;
      break;

    case ColorTone.MID:
    default:
      outerBgL = 0.95;
      imageBgL = 0.9;
      hoverBgL = 0.9;
      imageBgHover = 0.82;
      textL = 0.20;
      break;
  }

  const containerBg = adjustColorTone(src, outerBgL);
  const imageBg = adjustColorTone(src, imageBgL);
  const hoverBg = adjustColorTone(src, hoverBgL);
  const textToneBase = adjustColorTone(src, textL);
  const imageHover = adjustColorTone(src, imageBgHover);

  return {
    backgroundColor: containerBg.toHexString(),
    graphicContainerBg: imageBg.toHexString(),
    hoverColor: hoverBg.toHexString(),
    textColor: textToneBase.toHexString(),
    graphicContainerHoverBg: imageHover.toHexString(),
    srcColor: baseColorHex
  };
};

export const getReadableTextColorForBackground = (backgroundColorHex: string, baseColorToDeriveTextColorFrom: string): string => {
  if (isDarkColor(backgroundColorHex) || tinycolor.readability(backgroundColorHex, PMW_COLORS_NEUTRAL.NEUTRAL_0) > READABILITY_RATIO_THRESHOLD_FOR_WHITE) {
    return PMW_COLORS_NEUTRAL.NEUTRAL_0;
  }

  const veryDarkDerivedColor = adjustColorTone(tinycolor(baseColorToDeriveTextColorFrom), 0.2).toHexString();
  const darkerShades = [getDarkenedColorToneHex(baseColorToDeriveTextColorFrom, 0.25), getDarkenedColorToneHex(baseColorToDeriveTextColorFrom, 0.2), getDarkenedColorToneHex(baseColorToDeriveTextColorFrom, 0.3), veryDarkDerivedColor];
  // `lightenDarkenColor` returns a bare 6-digit hex by design (its poster-editor callers want it pound-less),
  // but a winning shade is returned verbatim below to callers that require a '#' — the event palette's `dark`
  // reaches a backend validator that anchors on it. Normalise here so every candidate leaves as a proper hex.
  const lighterShade = tinycolor(lightenDarkenColor(hexToRgba(baseColorToDeriveTextColorFrom))).toHexString();
  const shadesToTest = [...darkerShades, lighterShade];

  const sortedByReadability = shadesToTest.sort((color1, color2) => {
    return tinycolor.readability(backgroundColorHex, color2) - tinycolor.readability(backgroundColorHex, color1);
  });

  return tinycolor.readability(backgroundColorHex, sortedByReadability[0]) >= READABILITY_RATIO_THRESHOLD_FOR_COLOR ? sortedByReadability[0] : tinycolor.mostReadable(backgroundColorHex, [PMW_COLORS_NEUTRAL.NEUTRAL_0, PMW_COLORS_NEUTRAL.NEUTRAL_90], {
    includeFallbackColors: true,
    size: 'large',
    level: 'AA',
  }).toHexString();
};

export const classifyColorTone = (c: Instance): ColorTone => {
  const lum = c.getLuminance();

  if (lum <= 0.15) {
    return ColorTone.VERY_DARK;
  }
  if (lum <= 0.35) {
    return ColorTone.DARK;
  }

  if (lum >= 0.9) {
    return ColorTone.VERY_LIGHT;
  }

  if (lum >= 0.75) {
    return ColorTone.LIGHT;
  }

  return ColorTone.MID;
};

export const adjustColorTone = (src: Instance, lightness: number): Instance => {
  const hsl = src.toHsl();
  return tinycolor({
    h: hsl.h,
    s: Math.min(1, hsl.s * 0.95),
    l: Math.min(1, Math.max(0, lightness)),
  });
}

export const isDarkColor = (colorHex: string): boolean => {
  const classifiedColor = classifyColorTone(tinycolor(colorHex));
  return classifiedColor === ColorTone.DARK || classifiedColor === ColorTone.VERY_DARK;
};
